import React, { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Upload, FileText, X } from "lucide-react";
import { toast } from "sonner";
import { useBoundStore } from "../../store";
import { useI18n } from "../../i18n";
import { importPlaybook } from "../../services/Playbook";

interface ImportPlaybookDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const MAX_FILE_SIZE = 25 * 1024 * 1024; // 25MB in bytes

const ACCEPTED_EXTENSIONS = [".pdf", ".docx", ".xlsx", ".xls", ".txt"];

export default function ImportPlaybookDialog({ open, onOpenChange }: ImportPlaybookDialogProps) {
  const navigate = useNavigate();
  const { t } = useI18n();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const fetchPlaybooks = useBoundStore((state) => state.fetchPlaybooks);

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isPrivate, setIsPrivate] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  const validateFile = (file: File): string | null => {
    // Check file size
    if (file.size > MAX_FILE_SIZE) {
      return t.playbooks.importDialog.fileSizeExceeds(
        (file.size / 1024 / 1024).toFixed(2)
      );
    }

    // Check file extension
    const extension = "." + file.name.split(".").pop()?.toLowerCase();
    if (!ACCEPTED_EXTENSIONS.includes(extension)) {
      return t.playbooks.importDialog.invalidFileType;
    }

    return null;
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const error = validateFile(file);
    if (error) {
      toast.error(error);
      return;
    }

    setSelectedFile(file);
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleImport = async () => {
    if (!selectedFile) {
      toast.error(t.playbooks.importDialog.selectFileRequired);
      return;
    }

    setUploading(true);
    setUploadProgress(0);

    try {
      // Simulated progress: quick at first, then easing towards 95% so long
      // documents (minutes) still show movement
      const progressInterval = setInterval(() => {
        setUploadProgress((prev) =>
          Math.min(95, prev < 30 ? prev + 10 : prev + (95 - prev) * 0.04),
        );
      }, 700);

      const response = await importPlaybook(selectedFile, isPrivate);

      clearInterval(progressInterval);
      setUploadProgress(100);

      if (response.success && response.data) {
        toast.success(t.playbooks.importDialog.importSuccess);

        // Refresh playbooks list
        await fetchPlaybooks();

        // Reset form
        handleReset();
        onOpenChange(false);

        // Navigate to the new playbook
        navigate(`/playbooks/${response.data.id}`);
      } else {
        const errorMsg =
          response.errors?.[0]?.detail ||
          response.errors?.[0]?.title ||
          t.playbooks.importDialog.importFailed;
        toast.error(errorMsg);
      }
    } catch (error) {
      console.error("Error importing playbook:", error);
      toast.error(t.playbooks.importDialog.unexpectedError);
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleReset = () => {
    setSelectedFile(null);
    setIsPrivate(true);
    setUploadProgress(0);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleCancel = () => {
    handleReset();
    onOpenChange(false);
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + " B";
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + " KB";
    return (bytes / (1024 * 1024)).toFixed(2) + " MB";
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Upload className="h-5 w-5" />
            {t.playbooks.importDialog.title}
          </DialogTitle>
          <DialogDescription>
            {t.playbooks.importDialog.description}
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 py-4">
          {/* File Upload Area */}
          <div className="grid gap-2">
            <Label htmlFor="file">
              {t.playbooks.importDialog.fileLabel} <span className="text-red-500">*</span>
            </Label>

            {!selectedFile ? (
              <div className="flex flex-col items-center justify-center border-2 border-dashed border-gray-300 rounded-lg p-8 hover:border-gray-400 transition-colors">
                <Upload className="h-12 w-12 text-gray-400 mb-4" />
                <p className="text-sm text-muted-foreground mb-2">
                  {t.playbooks.importDialog.dropHint}
                </p>
                <p className="text-xs text-muted-foreground mb-4">
                  {t.playbooks.importDialog.formatsHint}
                </p>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => fileInputRef.current?.click()}
                >
                  {t.playbooks.importDialog.selectFile}
                </Button>
                <input
                  ref={fileInputRef}
                  type="file"
                  id="file"
                  accept=".pdf,.docx,.xlsx,.xls,.txt"
                  onChange={handleFileSelect}
                  className="hidden"
                />
              </div>
            ) : (
              <div className="flex items-center justify-between border rounded-lg p-4">
                <div className="flex items-center gap-3">
                  <FileText className="h-8 w-8" style={{ color: '#016737' }} />
                  <div>
                    <p className="font-medium">{selectedFile.name}</p>
                    <p className="text-sm text-muted-foreground">{formatFileSize(selectedFile.size)}</p>
                  </div>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={handleRemoveFile}
                  disabled={uploading}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            )}
          </div>

          {/* Upload Progress */}
          {uploading && (
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span>{t.playbooks.importDialog.uploadingProcessing}</span>
                <span>{Math.round(uploadProgress)}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="h-2 rounded-full transition-all duration-300"
                  style={{ backgroundColor: '#016737', width: `${uploadProgress}%` }}
                />
              </div>
              <p className="text-xs text-muted-foreground">
                {t.playbooks.importDialog.longDocumentHint}
              </p>
            </div>
          )}

          {/* Privacy Toggle */}
          <div className="flex items-center justify-between rounded-lg border p-4">
            <div className="space-y-0.5">
              <Label htmlFor="isPrivate">{t.playbooks.importDialog.privateLabel}</Label>
              <p className="text-sm text-muted-foreground">
                {isPrivate
                  ? t.playbooks.importDialog.privateOnlyYou
                  : t.playbooks.importDialog.privateEveryone}
              </p>
            </div>
            <Switch id="isPrivate" checked={isPrivate} onCheckedChange={setIsPrivate} disabled={uploading} />
          </div>
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={handleCancel} disabled={uploading}>
            {t.playbooks.importDialog.cancel}
          </Button>
          <Button type="button" onClick={handleImport} disabled={!selectedFile || uploading}>
            {uploading ? t.playbooks.importDialog.importing : t.playbooks.importDialog.submit}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
